import React, { useMemo, useState } from "react";
import { Row, Col, Card, CardBody, CardHeader, Table, Badge } from "reactstrap";
import ReactApexChart from "react-apexcharts";
import moment from "moment";
import "moment/locale/es";
import { formatCurrency } from "../../utils/formatCurrency";
import PeriodSelector from "./PeriodSelector";
import ExpandableChart from "./ExpandableChart";
import { CategoryDonut, useCategories } from "./CategoryBreakdown";
import { sinDiaEnCurso } from "./partialDay";
import { TOOLBAR_DESCARGA, LEYENDA, SIN_LEYENDA, Referencia } from "./graficos";
import { BotonesExportar } from "./exportar";
import { TITULO_CARD, TITULO_ACLARACION } from "./tituloCard";
// Misma paleta que el resto de la pantalla: el color se pide por concepto.
import { ESTADO, INTENSIDAD, MODALIDAD, ORIGEN, UI } from "../Stats2/theme";

const fmt = (v) => formatCurrency(v ?? 0, true);

// ── KPI summary cards ────────────────────────────────────────
// Las mismas palabras que el gráfico de más abajo y que la tabla del final. Un
// mismo número no puede llamarse "Salidas" en la tarjeta y "Autos que salieron"
// en la leyenda del gráfico que tiene debajo: el dueño no tiene forma de saber
// que son lo mismo, y en esta pantalla "Salidas de caja" son pesos que salen.
const SUMMARY_ITEMS = [
  { key: "totalIn", label: "Autos que entraron", icon: "login", color: "success" },
  { key: "totalOut", label: "Autos que salieron", icon: "logout", color: "primary" },
  { key: "avgPerDay", label: "Promedio por día", icon: "calendar_today", color: "info", isNumber: true },
  { key: "peakDay", label: "Día más movido", icon: "bolt", color: "warning", isText: true },
  { key: "totalCanceled", label: "Tickets anulados", icon: "block", color: "danger", isNumber: true },
  { key: "cancelRate", label: "Tasa de cancelación", icon: "percent", color: "secondary", isPercent: true },
];

const TicketSummary = ({ docs }) => {
  const stats = useMemo(() => {
    const totalIn = docs.reduce((a, d) => a + (d.tickets?.total?.in || 0), 0);
    const totalOut = docs.reduce((a, d) => a + (d.tickets?.total?.out || 0), 0);
    const totalCanceled = docs.reduce((a, d) => a + (d.canceled_count || 0), 0);
    // Promedio sobre días COMPLETOS: el total sí incluye el día en curso, pero
    // dividir por él como si fuera un día entero baja el promedio toda la
    // mañana. Mismo criterio que las otras pestañas (ver partialDay.js).
    const completos = sinDiaEnCurso(docs);
    const inCompletos = completos.reduce((a, d) => a + (d.tickets?.total?.in || 0), 0);
    const avgPerDay = completos.length > 0
      ? Math.round(inCompletos / completos.length)
      : (docs.length > 0 ? Math.round(totalIn / docs.length) : 0);
    const cancelRate = totalIn > 0 ? (totalCanceled / totalIn) * 100 : 0;

    // Find peak day
    let peakDay = "-";
    let peakVal = 0;
    docs.forEach((d) => {
      const val = d.tickets?.total?.in || 0;
      if (val > peakVal) { peakVal = val; peakDay = moment(d.date).format("ddd DD/MM"); }
    });

    return { totalIn, totalOut, avgPerDay, peakDay: `${peakDay} (${peakVal})`, totalCanceled, cancelRate };
  }, [docs]);

  return (
    <Row className="g-3 mb-3">
      {SUMMARY_ITEMS.map((item) => {
        const value = stats[item.key];
        const borderColor = `var(--bs-${item.color})`;
        return (
          <Col xl={2} lg={4} md={4} sm={6} xs={6} key={item.key}>
            <Card className="shadow-sm h-100 mb-0" style={{ borderLeft: `3px solid ${borderColor}` }}>
              <CardBody className="py-3 px-3">
                <div className="text-muted mb-2" style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 0.5 }}>
                  <span className="material-symbols-rounded me-1" style={{ fontSize: 14 }}>{item.icon}</span>
                  {item.label}
                </div>
                <h4 className={`text-${item.color} mb-0`} style={item.isText ? { fontSize: 16 } : undefined}>
                  {item.isPercent ? `${value.toFixed(1)}%` : item.isNumber ? value : item.isText ? value : value}
                </h4>
              </CardBody>
            </Card>
          </Col>
        );
      })}
    </Row>
  );
};

// ── Entries/Exits trend ──────────────────────────────────────
const TicketsTrend = ({ docs, heightOverride }) => {
  const sorted = useMemo(() => [...docs].sort((a, b) => a.date.localeCompare(b.date)), [docs]);

  const options = useMemo(
    () => ({
      chart: { type: "bar", height: heightOverride || 300, ...TOOLBAR_DESCARGA, fontFamily: "inherit", animations: { dynamicAnimation: { enabled: false } } },
      // Entradas y salidas son el mismo concepto contado dos veces —tickets—,
      // así que van del mismo azul en dos intensidades y no en dos identidades
      // distintas. Los cancelados no son un origen: son ESTADO, que es el banco
      // de "esto hay que mirarlo".
      colors: [INTENSIDAD.normal, INTENSIDAD.flojo, ESTADO.anulado.color],
      plotOptions: { bar: { columnWidth: "55%", borderRadius: 3 } },
      dataLabels: { enabled: false },
      xaxis: {
        categories: sorted.map((d) => moment(d.date).format("DD/MM")),
        labels: { style: { fontSize: "10px" }, rotate: -45, rotateAlways: sorted.length > 30 },
      },
      yaxis: { labels: { style: { fontSize: "11px" } } },
      tooltip: { shared: true, intersect: false },
      // Tres barras por día, una al lado de la otra: sin leyenda no se sabe
      // cuál es la que entró, cuál la que salió y cuál la que se anuló.
      legend: LEYENDA,
      grid: { borderColor: "#f1f1f1" },
    }),
    [sorted]
  );

  const series = useMemo(
    () => [
      // "Salidas" a secas se confundía con "Salidas de caja", que en esta misma
      // pantalla son los egresos de plata. Y "Cancelados" no es la palabra que
      // usa el sistema en el resto de la pantalla, que dice "anulados".
      { name: "Autos que entraron", data: sorted.map((d) => d.tickets?.total?.in || 0) },
      { name: "Autos que salieron", data: sorted.map((d) => d.tickets?.total?.out || 0) },
      { name: "Tickets anulados", data: sorted.map((d) => d.canceled_count || 0) },
    ],
    [sorted]
  );

  return (
    <Card className="shadow-sm mb-3">
      <CardHeader className="bg-white py-2">
        <h6 className="mb-0" style={TITULO_CARD}>
          <span className="material-symbols-rounded me-1" style={{ fontSize: 15, verticalAlign: "middle" }}>swap_horiz</span>
          Cuántos autos entraron y salieron cada día
        </h6>
      </CardHeader>
      <CardBody className="pt-1">
        <ReactApexChart options={options} series={series} type="bar" height={heightOverride || 300} />
      </CardBody>
    </Card>
  );
};

// ── Cancellation analysis with trend chart ───────────────────
const CancellationAnalysis = ({ docs, heightOverride }) => {
  const sorted = useMemo(() => [...docs].sort((a, b) => a.date.localeCompare(b.date)), [docs]);

  const stats = useMemo(() => {
    const totalIn = docs.reduce((a, d) => a + (d.tickets?.total?.in || 0), 0);
    const totalCanceled = docs.reduce((a, d) => a + (d.canceled_count || 0), 0);
    const rate = totalIn > 0 ? (totalCanceled / totalIn) * 100 : 0;

    const totalOut = docs.reduce((a, d) => a + (d.tickets?.total?.out || 0), 0);
    const totalAmount = docs.reduce((a, d) => a + (d.tickets?.total?.amount || 0), 0);
    const avgTicket = totalOut > 0 ? totalAmount / totalOut : 0;
    const lostRevenue = avgTicket * totalCanceled;

    // Trend: first half vs second half
    const half = Math.floor(docs.length / 2);
    const first = docs.slice(half);
    const second = docs.slice(0, half);
    const firstRate = (() => {
      const inn = first.reduce((a, d) => a + (d.tickets?.total?.in || 0), 0);
      const canc = first.reduce((a, d) => a + (d.canceled_count || 0), 0);
      return inn > 0 ? (canc / inn) * 100 : 0;
    })();
    const secondRate = (() => {
      const inn = second.reduce((a, d) => a + (d.tickets?.total?.in || 0), 0);
      const canc = second.reduce((a, d) => a + (d.canceled_count || 0), 0);
      return inn > 0 ? (canc / inn) * 100 : 0;
    })();
    const rateTrend = secondRate - firstRate;

    return { totalCanceled, rate, lostRevenue, rateTrend };
  }, [docs]);

  // Tasa de anulacion por dia.
  //
  // Antes esto hacia Math.min(rate, 50): un dia con 80% de anulaciones se
  // dibujaba como 50%. Achataba justo el dia que hay que mirar — y el dia raro
  // no es ruido en esta serie, ES la senal. Ademas era redundante: el yMax de
  // abajo ya usa el percentil 95 para que un pico no aplaste al resto.
  const rateData = useMemo(() => {
    return sorted.map((d) => {
      const inn = d.tickets?.total?.in || 0;
      const canc = d.canceled_count || 0;
      if (inn === 0) return 0;
      return parseFloat(((canc / inn) * 100).toFixed(1));
    });
  }, [sorted]);

  // Smart Y-axis max: use the 95th percentile to avoid outliers stretching the chart
  const yMax = useMemo(() => {
    const values = rateData.filter((v) => v > 0).sort((a, b) => a - b);
    if (!values.length) return 20;
    const p95 = values[Math.floor(values.length * 0.95)] || values[values.length - 1];
    // Round up to nearest 5, minimum 10
    return Math.max(10, Math.ceil(Math.max(p95, stats.rate * 1.5) / 5) * 5);
  }, [rateData, stats.rate]);

  const chartOptions = useMemo(
    () => ({
      chart: { type: "area", height: heightOverride || 180, sparkline: { enabled: false }, ...TOOLBAR_DESCARGA, fontFamily: "inherit", animations: { dynamicAnimation: { enabled: false } } },
      // Todo el gráfico habla de tickets anulados: el ámbar de ESTADO.
      colors: [ESTADO.anulado.color],
      stroke: { curve: "smooth", width: 2 },
      fill: { type: "gradient", gradient: { shadeIntensity: 1, opacityFrom: 0.3, opacityTo: 0.05 } },
      dataLabels: { enabled: false },
      xaxis: {
        categories: sorted.map((d) => moment(d.date).format("DD/MM")),
        labels: { style: { fontSize: "9px" }, rotate: -45, rotateAlways: sorted.length > 20 },
      },
      yaxis: {
        labels: { formatter: (v) => `${v.toFixed(0)}%`, style: { fontSize: "10px" } },
        min: 0,
        max: yMax,
        tickAmount: Math.min(5, yMax / 5),
      },
      tooltip: { y: { formatter: (v) => `${v.toFixed(1)}%` } },
      // Una sola serie, y la línea punteada del promedio lleva su propio
      // rótulo escrito al costado ("Promedio: 4,2%"): no hay color que
      // explicar.
      legend: SIN_LEYENDA,
      grid: { borderColor: "#f1f1f1" },
      annotations: {
        yaxis: [{
          y: stats.rate,
          // El promedio es una referencia, no una alarma: va en el gris de la
          // pantalla. En rojo, un promedio normal parecía un problema.
          borderColor: UI.textoSuave,
          strokeDashArray: 4,
          label: {
            text: `Promedio: ${stats.rate.toFixed(1)}%`,
            style: { fontSize: "10px", color: UI.textoSuave, background: "#fff" },
            position: "right",
            offsetX: -10,
          },
        }],
      },
    }),
    [sorted, stats.rate, yMax]
  );

  return (
    <Card className="shadow-sm mb-3">
      <CardHeader className="bg-white py-2">
        <h6 className="mb-0" style={TITULO_CARD}>
          <span className="material-symbols-rounded me-1 text-warning" style={{ fontSize: 15, verticalAlign: "middle" }}>block</span>
          Cuántos tickets se cancelaron y qué peso tienen
        </h6>
      </CardHeader>
      <CardBody>
        <Row className="text-center g-3 mb-3">
          <Col xs={6} md={3}>
            <div className="text-muted" style={{ fontSize: 11 }}>CANCELADOS</div>
            <h4 className="text-warning mb-0">{stats.totalCanceled}</h4>
            <div className="text-muted" style={{ fontSize: 10 }}>tickets anulados en el período</div>
          </Col>
          <Col xs={6} md={3}>
            <div className="text-muted" style={{ fontSize: 11 }}>TASA</div>
            <h4 className={`mb-0 ${stats.rate > 5 ? "text-danger" : stats.rate > 2 ? "text-warning" : "text-success"}`}>
              {stats.rate.toFixed(1)}%
            </h4>
            <div className="text-muted" style={{ fontSize: 10 }}>sobre las entradas del período</div>
          </Col>
          <Col xs={6} md={3}>
            {/* NO es plata perdida medida: es el ticket promedio multiplicado
                por la cantidad de anulados. Muchas anulaciones son correcciones
                de carga, no plata que se fue. Presentarlo en rojo y con formato
                de plata lo hacia pasar por un hecho. Va en gris, como lo que
                es: una hipotesis para ir a mirar. */}
            <div className="text-muted" style={{ fontSize: 11 }}>SI TODOS FUERAN COBRABLES</div>
            <h4 className="text-muted mb-0">{fmt(stats.lostRevenue)}</h4>
            <div className="text-muted" style={{ fontSize: 10 }}>estimado sobre el ticket promedio</div>
          </Col>
          <Col xs={6} md={3}>
            <div className="text-muted" style={{ fontSize: 11 }}>TENDENCIA</div>
            <h4 className={`mb-0 ${stats.rateTrend <= 0 ? "text-success" : "text-danger"}`}>
              <span className="material-symbols-rounded" style={{ fontSize: 20 }}>
                {stats.rateTrend <= 0 ? "trending_down" : "trending_up"}
              </span>
              {stats.rateTrend <= 0 ? " Mejorando" : " Empeorando"}
            </h4>
            <div className="text-muted" style={{ fontSize: 10 }}>
              {stats.rateTrend <= 0
                ? "se cancela menos que en la primera mitad del período"
                : "se cancela más que en la primera mitad del período"}
            </div>
          </Col>
        </Row>
        {sorted.length >= 7 && (
          <>
            <div className="mb-1" style={{ fontSize: 12.5, fontWeight: 600, color: UI.texto }}>
              Cuánto se canceló cada día
              <span className="ms-1" style={{ fontSize: 11.5, fontWeight: 400, color: UI.textoSuave }}>
                (la línea roja punteada es el promedio del período)
              </span>
            </div>
            <ReactApexChart options={chartOptions} series={[{ name: "Tasa de cancelación", data: rateData }]} type="area" height={heightOverride || 180} />
          </>
        )}
      </CardBody>
    </Card>
  );
};

// ── Tickets by day of week ───────────────────────────────────
const DAY_NAMES = ["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"];
// Los tres colores del gráfico significan algo, y se nombran una sola vez para
// que las barras y la referencia de abajo no se puedan desincronizar.
// Los siete días son lo mismo medido siete veces, así que van del mismo tono
// graduado: el más movido más oscuro, el más flojo más claro. En verde y rojo,
// el día más flojo de la semana se leía como una pérdida.
const DIA_MAS_MOVIDO = INTENSIDAD.fuerte;
const DIA_MAS_FLOJO = INTENSIDAD.flojo;
const DIA_RESTO = INTENSIDAD.normal;

// Todos los días que no son el mejor ni el peor van del mismo azul. Antes el
// sábado y el domingo iban del verde de "el día más movido": un fin de semana
// cualquiera se leía como si fuera el mejor día del negocio. Que sean fin de
// semana ya lo dice el eje.
const DAY_COLORS_WD = [DIA_RESTO, DIA_RESTO, DIA_RESTO, DIA_RESTO, DIA_RESTO, DIA_RESTO, DIA_RESTO];

const TicketsByDayOfWeek = ({ docs, heightOverride }) => {
  const { avgTickets, bestIdx, worstIdx } = useMemo(() => {
    const buckets = {};
    for (let i = 1; i <= 7; i++) buckets[i] = [];

    docs.forEach((d) => {
      const dow = moment(d.date).isoWeekday();
      buckets[dow].push(d.tickets?.total?.in || 0);
    });

    const avg = (arr) => (arr.length ? Math.round(arr.reduce((a, b) => a + b, 0) / arr.length) : 0);
    const avgs = DAY_NAMES.map((_, i) => avg(buckets[i + 1]));

    let bestIdx = 0, worstIdx = 0;
    avgs.forEach((v, i) => {
      if (v > avgs[bestIdx]) bestIdx = i;
      if (v < avgs[worstIdx]) worstIdx = i;
    });

    return { avgTickets: avgs, bestIdx, worstIdx };
  }, [docs]);

  const colors = useMemo(() => {
    return DAY_NAMES.map((_, i) => {
      if (i === bestIdx) return DIA_MAS_MOVIDO;
      if (i === worstIdx) return DIA_MAS_FLOJO;
      return DAY_COLORS_WD[i];
    });
  }, [bestIdx, worstIdx]);

  const options = useMemo(
    () => ({
      chart: { type: "bar", height: heightOverride || 260, ...TOOLBAR_DESCARGA, fontFamily: "inherit", animations: { dynamicAnimation: { enabled: false } } },
      colors,
      plotOptions: { bar: { columnWidth: "55%", borderRadius: 4, distributed: true } },
      dataLabels: { enabled: true, formatter: (v) => v, style: { fontSize: "11px", colors: [UI.texto] }, offsetY: -15 },
      xaxis: { categories: DAY_NAMES, labels: { style: { fontSize: "12px" } } },
      yaxis: { labels: { style: { fontSize: "11px" } } },
      tooltip: { y: { formatter: (v) => `${v} tickets` } },
      // Con `distributed: true` la leyenda de ApexCharts lista las categorías
      // —Lun, Mar, Mié…—, que ya están en el eje. Lo que hay que explicar es
      // qué quiere decir cada color, y eso va abajo con <Referencia>.
      legend: SIN_LEYENDA,
      grid: { borderColor: "#f1f1f1" },
    }),
    [colors]
  );

  if (!docs.length) return null;

  return (
    <Card className="shadow-sm h-100">
      <CardHeader className="bg-white py-2">
        <h6 className="mb-0" style={TITULO_CARD}>
          <span className="material-symbols-rounded me-1" style={{ fontSize: 15, verticalAlign: "middle" }}>calendar_view_week</span>
          Qué días de la semana te mueven más autos
        </h6>
      </CardHeader>
      <CardBody className="pt-1">
        <ReactApexChart options={options} series={[{ name: "Tickets promedio", data: avgTickets }]} type="bar" height={heightOverride || 260} />
        {/* La referencia nombra los tres colores, no sólo dos: el azul del
            resto de la semana también necesitaba decirse. */}
        <Referencia
          items={[
            { color: DIA_MAS_MOVIDO, texto: `El día más movido: ${DAY_NAMES[bestIdx]}` },
            { color: DIA_MAS_FLOJO, texto: `El más flojo: ${DAY_NAMES[worstIdx]}` },
            { color: DIA_RESTO, texto: "El resto de la semana" },
          ]}
        />
      </CardBody>
    </Card>
  );
};

// ── Ticket type mix over time ────────────────────────────────
const TicketTypeMix = ({ docs, heightOverride }) => {
  const sorted = useMemo(() => [...docs].sort((a, b) => a.date.localeCompare(b.date)), [docs]);

  const hasMultipleTypes = useMemo(() => {
    const types = new Set();
    sorted.forEach((d) => {
      if (d.tickets?.hour?.in > 0) types.add("hour");
      if (d.tickets?.stay?.in > 0) types.add("stay");
      if (d.tickets?.tn?.in > 0) types.add("tn");
      if (d.tickets?.plana?.in > 0) types.add("plana");
    });
    return types.size > 1;
  }, [sorted]);

  const options = useMemo(
    () => ({
      chart: { type: "bar", stacked: true, stackType: "100%", height: heightOverride || 260, ...TOOLBAR_DESCARGA, fontFamily: "inherit", animations: { dynamicAnimation: { enabled: false } } },
      // Las cuatro son modalidades de cobro del ticket: la rampa del azul de
      // Tickets, ordenada por duración, y no cuatro tonos sueltos.
      colors: [MODALIDAD.hour.color, MODALIDAD.stay.color, MODALIDAD.tn.color, MODALIDAD.plana.color],
      plotOptions: { bar: { columnWidth: "65%" } },
      dataLabels: { enabled: false },
      xaxis: {
        categories: sorted.map((d) => moment(d.date).format("DD/MM")),
        labels: { style: { fontSize: "10px" }, rotate: -45, rotateAlways: sorted.length > 30 },
      },
      yaxis: { labels: { formatter: (v) => `${v.toFixed(0)}%`, style: { fontSize: "11px" } } },
      // Cuatro franjas apiladas al 100%: la leyenda es lo único que dice qué
      // modalidad de cobro es cada una.
      legend: LEYENDA,
      grid: { borderColor: "#f1f1f1" },
      tooltip: {
        y: { formatter: (v, { dataPointIndex, w }) => {
          const total = w.globals.stackedSeriesTotals[dataPointIndex];
          const pct = total > 0 ? ((v / total) * 100).toFixed(1) : 0;
          return `${v} (${pct}%)`;
        }},
      },
    }),
    [sorted]
  );

  const series = useMemo(
    () => [
      { name: "Por hora", data: sorted.map((d) => d.tickets?.hour?.in || 0) },
      { name: "Por estadía", data: sorted.map((d) => d.tickets?.stay?.in || 0) },
      { name: "Por turno", data: sorted.map((d) => d.tickets?.tn?.in || 0) },
      { name: "Tarifa plana", data: sorted.map((d) => d.tickets?.plana?.in || 0) },
    ],
    [sorted]
  );

  if (!hasMultipleTypes) return null;

  return (
    <Card className="shadow-sm">
      <CardHeader className="bg-white py-2">
        {/* "Estadía" a secas parecía contestar cuánto se queda la gente. El
            grupo son MODALIDADES DE COBRO, y el título ahora lo dice. */}
        <h6 className="mb-0" style={TITULO_CARD}>
          <span className="material-symbols-rounded me-1" style={{ fontSize: 15, verticalAlign: "middle" }}>donut_small</span>
          Cómo se cobró
          <span className="ms-1" style={TITULO_ACLARACION}>
            (qué parte de los tickets de cada día fue con cada modalidad)
          </span>
        </h6>
      </CardHeader>
      <CardBody className="pt-1">
        <ReactApexChart options={options} series={series} type="bar" height={heightOverride || 260} />
      </CardBody>
    </Card>
  );
};

// ── Peak days ranking ────────────────────────────────────────
const PeakDaysRanking = ({ docs }) => {
  const { busiest, slowest } = useMemo(() => {
    const sorted = [...docs]
      .map((d) => ({
        date: d.date,
        label: moment(d.date).format("ddd DD/MM"),
        ticketsIn: d.tickets?.total?.in || 0,
        income: d.income || 0,
      }))
      .sort((a, b) => b.ticketsIn - a.ticketsIn);

    return {
      busiest: sorted.slice(0, 5),
      slowest: sorted.slice(-5).reverse(),
    };
  }, [docs]);

  if (!docs.length) return null;

  const RankList = ({ items, color, icon }) => (
    <div>
      {items.map((d, i) => (
        <div
          key={d.date}
          className="d-flex align-items-center justify-content-between py-1"
          style={{ fontSize: 13, borderBottom: i < items.length - 1 ? "1px solid #f1f1f1" : "none" }}
        >
          <div className="d-flex align-items-center">
            <span
              className="fw-bold me-2"
              style={{ fontSize: 11, width: 18, textAlign: "center", color }}
            >
              {i + 1}
            </span>
            <span>{d.label}</span>
          </div>
          <div className="text-end">
            <span className="fw-semibold">{d.ticketsIn}</span>
            <span className="text-muted ms-2" style={{ fontSize: 11 }}>{fmt(d.income)}</span>
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <Card className="shadow-sm h-100">
      <CardHeader className="bg-white py-2">
        <h6 className="mb-0" style={TITULO_CARD}>
          <span className="material-symbols-rounded me-1" style={{ fontSize: 15, verticalAlign: "middle" }}>emoji_events</span>
          Tus cinco mejores días y tus cinco más flojos
          <span className="ms-1" style={TITULO_ACLARACION}>
            (autos que entraron, y lo que dejó el día)
          </span>
        </h6>
      </CardHeader>
      <CardBody>
        <div className="mb-1" style={{ fontSize: 12, fontWeight: 700, color: INTENSIDAD.fuerte }}>
          <span className="material-symbols-rounded me-1" style={{ fontSize: 13 }}>arrow_upward</span>
          Los más movidos
        </div>
        <RankList items={busiest} color={INTENSIDAD.fuerte} />
        <hr className="my-2" />
        <div className="mb-1" style={{ fontSize: 12, fontWeight: 700, color: ORIGEN.tickets.color }}>
          <span className="material-symbols-rounded me-1" style={{ fontSize: 13 }}>arrow_downward</span>
          Los más flojos
        </div>
        {/* El tono más claro de la rampa no se puede usar en TEXTO: sobre
            blanco no llega al contraste que necesita una letra chica. Los dos
            títulos se separan igual porque uno es más oscuro que el otro. */}
        <RankList items={slowest} color={ORIGEN.tickets.color} />
      </CardBody>
    </Card>
  );
};

// ── Daily detail table ──────────────────────────────────────
const DailyDetailTable = ({ docs }) => {
  const [sortKey, setSortKey] = useState("date");
  const [sortAsc, setSortAsc] = useState(false);
  const [collapsed, setCollapsed] = useState(true);

  const rows = useMemo(() => {
    return docs.map((d) => {
      const ticketsIn = d.tickets?.total?.in || 0;
      const ticketsOut = d.tickets?.total?.out || 0;
      const canceled = d.canceled_count || 0;
      const cancelRate = ticketsIn > 0 ? (canceled / ticketsIn) * 100 : 0;
      const ticketAmount = d.tickets?.total?.amount || 0;
      return {
        date: d.date,
        dateLabel: moment(d.date).format("ddd DD/MM"),
        ticketsIn,
        ticketsOut,
        canceled,
        cancelRate,
        ticketAmount,
      };
    });
  }, [docs]);

  const sorted = useMemo(() => {
    return [...rows].sort((a, b) => {
      const va = a[sortKey];
      const vb = b[sortKey];
      if (typeof va === "string") return sortAsc ? va.localeCompare(vb) : vb.localeCompare(va);
      return sortAsc ? va - vb : vb - va;
    });
  }, [rows, sortKey, sortAsc]);

  // El dia en curso queda afuera: a las 9 de la manana SIEMPRE seria el "menos
  // activo", y esa medalla no dice nada del dia, dice que todavia no termino.
  const cerrados = useMemo(() => sinDiaEnCurso(rows), [rows]);
  const bestDay = useMemo(() => cerrados.reduce((best, r) => r.ticketsIn > best.ticketsIn ? r : best, cerrados[0]), [cerrados]);
  const worstDay = useMemo(() => cerrados.reduce((worst, r) => r.ticketsIn < worst.ticketsIn ? r : worst, cerrados[0]), [cerrados]);

  const displayed = collapsed ? sorted.slice(0, 10) : sorted;

  const handleSort = (key) => {
    if (sortKey === key) setSortAsc(!sortAsc);
    else { setSortKey(key); setSortAsc(key === "date"); }
  };

  const SortIcon = ({ col }) => {
    if (sortKey !== col) return <span className="material-symbols-rounded text-muted opacity-25 ms-1" style={{ fontSize: 12 }}>unfold_more</span>;
    return (
      <span className="material-symbols-rounded ms-1" style={{ fontSize: 12 }}>
        {sortAsc ? "arrow_upward" : "arrow_downward"}
      </span>
    );
  };

  // Seis columnas en una tabla que se lee en el teléfono: acá van los verbos
  // solos y no la frase entera de las tarjetas. Es el mismo vocabulario
  // —"salieron" no se confunde con "salidas de caja"— en la versión corta que
  // entra en un encabezado de columna.
  const columns = [
    { key: "date", label: "Fecha", align: "start" },
    { key: "ticketsIn", label: "Entraron", align: "end" },
    { key: "ticketsOut", label: "Salieron", align: "end" },
    { key: "canceled", label: "Anulados", align: "end" },
    { key: "cancelRate", label: "Tasa de cancelación", align: "end" },
    { key: "ticketAmount", label: "Ingreso tickets", align: "end" },
  ];

  if (!rows.length) return null;

  return (
    <Card className="shadow-sm mt-3">
      <CardHeader className="bg-white py-2 d-flex align-items-center justify-content-between">
        <h6 className="mb-0" style={TITULO_CARD}>
          <span className="material-symbols-rounded me-1" style={{ fontSize: 15, verticalAlign: "middle" }}>table_chart</span>
          Día por día, el detalle completo
          <span className="ms-1" style={TITULO_ACLARACION}>
            ({rows.length} días{rows.length > 3 ? " · el más movido y el más flojo van marcados" : ""})
          </span>
        </h6>
        <BotonesExportar
          columnas={columns.map((c) => (c.key === "date"
            ? { ...c, valor: (f) => moment(f.date).format("DD/MM/YYYY") }
            : c))}
          filas={sorted}
          nombre="detalle-diario-tickets"
        />
      </CardHeader>
      <CardBody className="py-0 px-0">
        <div style={{ maxHeight: collapsed ? undefined : 500, overflowY: collapsed ? undefined : "auto" }}>
          <Table responsive hover size="sm" className="mb-0" style={{ fontSize: 13 }}>
            <thead className="table-light" style={{ position: "sticky", top: 0, zIndex: 1 }}>
              <tr>
                {columns.map((col) => (
                  <th
                    key={col.key}
                    className={`text-${col.align}`}
                    style={{ cursor: "pointer", userSelect: "none", whiteSpace: "nowrap" }}
                    onClick={() => handleSort(col.key)}
                  >
                    {col.label}<SortIcon col={col.key} />
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {displayed.map((r) => {
                const isBest = bestDay && r.date === bestDay.date && rows.length > 3;
                const isWorst = worstDay && r.date === worstDay.date && rows.length > 3;
                return (
                  <tr key={r.date} style={isBest ? { backgroundColor: "rgba(52,195,143,0.08)" } : isWorst ? { backgroundColor: "rgba(244,106,106,0.08)" } : undefined}>
                    <td>
                      {r.dateLabel}
                      {isBest && <Badge color="success" pill className="ms-2" style={{ fontSize: 10 }}>El más movido</Badge>}
                      {isWorst && <Badge color="danger" pill className="ms-2" style={{ fontSize: 10 }}>El más flojo</Badge>}
                    </td>
                    <td className="text-end">{r.ticketsIn}</td>
                    <td className="text-end">{r.ticketsOut}</td>
                    <td className={`text-end ${r.canceled > 0 ? "text-warning" : ""}`}>{r.canceled}</td>
                    <td className={`text-end ${r.cancelRate > 5 ? "text-danger" : r.cancelRate > 2 ? "text-warning" : ""}`}>
                      {r.cancelRate.toFixed(1)}%
                    </td>
                    <td className="text-end text-success">{fmt(r.ticketAmount)}</td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </div>
        {rows.length > 10 && (
          <div className="text-center py-2 border-top">
            <button
              className="btn btn-sm btn-link text-muted text-decoration-none"
              onClick={() => setCollapsed(!collapsed)}
            >
              <span className="material-symbols-rounded me-1" style={{ fontSize: 14, verticalAlign: "middle" }}>
                {collapsed ? "expand_more" : "expand_less"}
              </span>
              {collapsed ? `Ver todos (${rows.length})` : "Ver menos"}
            </button>
          </div>
        )}
      </CardBody>
    </Card>
  );
};

// ── Mix + Categories (conditional layout) ───────────────────
const TicketTypeMixAndCategories = ({ docs }) => {
  const categories = useCategories(docs);
  const hasCategories = categories && categories.length > 0;

  // Check if TicketTypeMix would render (needs multiple types)
  const hasMultipleTypes = useMemo(() => {
    const types = new Set();
    docs.forEach((d) => {
      if (d.tickets?.hour?.in > 0) types.add("hour");
      if (d.tickets?.stay?.in > 0) types.add("stay");
      if (d.tickets?.tn?.in > 0) types.add("tn");
      if (d.tickets?.plana?.in > 0) types.add("plana");
    });
    return types.size > 1;
  }, [docs]);

  if (!hasMultipleTypes && !hasCategories) return null;

  if (hasMultipleTypes && hasCategories) {
    return (
      <Row className="g-3 mb-3">
        <Col xl={6}>
          <ExpandableChart title="Cómo se cobró">
            <TicketTypeMix docs={docs} />
          </ExpandableChart>
        </Col>
        <Col xl={6}>
          <CategoryDonut docs={docs} />
        </Col>
      </Row>
    );
  }

  // Only one of them renders — full width
  return (
    <Row className="g-3 mb-3">
      <Col xl={12}>
        {hasMultipleTypes ? (
          <ExpandableChart title="Cómo se cobró">
            <TicketTypeMix docs={docs} />
          </ExpandableChart>
        ) : (
          <CategoryDonut docs={docs} heightOverride={300} />
        )}
      </Col>
    </Row>
  );
};

// ── Tab principal ────────────────────────────────────────────
const TabTickets = ({ dailyDocs, periodo = null }) => {
    // `periodo` es OPCIONAL y viene de afuera. Si llega, manda el de afuera y
  // este componente oculta su propio selector: dos selectores mostrando
  // periodos distintos en la misma vista es justo la incoherencia que
  // estamos sacando de esta pantalla. Si no llega —que es como lo usa la
  // pantalla de siempre— se comporta exactamente igual que antes.
  const [periodLocal, setPeriod] = useState(30);
  const period = periodo ?? periodLocal;

  const filtered = useMemo(
    () => dailyDocs.slice(0, period),
    [dailyDocs, period]
  );

  return (
    <>
      <div className="mb-3">
        {periodo == null && (
          <PeriodSelector value={period} onChange={setPeriod} maxDays={dailyDocs.length} />
        )}
      </div>

      <TicketSummary docs={filtered} />
      <ExpandableChart title="Cuántos autos entraron y salieron cada día">
        <TicketsTrend docs={filtered} />
      </ExpandableChart>
      <ExpandableChart title="Cuántos tickets se cancelaron y qué peso tienen">
        <CancellationAnalysis docs={filtered} />
      </ExpandableChart>

      <Row className="g-3 mb-3">
        <Col xl={6}>
          <ExpandableChart title="Qué días de la semana te mueven más autos">
            <TicketsByDayOfWeek docs={filtered} />
          </ExpandableChart>
        </Col>
        <Col xl={6}>
          <PeakDaysRanking docs={filtered} />
        </Col>
      </Row>

      <TicketTypeMixAndCategories docs={filtered} />

      <DailyDetailTable docs={filtered} />
    </>
  );
};

export default TabTickets;
